/**
 * M6.5-W3b — Kladblok-canvas (Notepad) voor Base-WP.
 *
 * Eenvoudig werkvlak waar admin folder-concepten op kan slepen vanaf de
 * Foldertree. Dient ALLEEN om relaties tussen folder-objecten binnen
 * dezelfde folder te tekenen. Geen viewstate-persist:
 *  - Nodes leven in memory-only state; bij component-unmount of WP-switch
 *    verdwijnen ze.
 *  - Relaties tussen die nodes worden WEL persistent opgeslagen via
 *    POST /relations.
 *
 * V1-light (deze iteratie):
 *  - Drag-from-Foldertree (MIME `application/ae-existing-concept-id`)
 *    → tijdelijke node op drop-positie.
 *  - Klik op node → useUi.selectConcept(id) opent AttributePanel.
 *  - Connect (drag van handle naar andere node) → prompt voor
 *    relation-type-id → POST /relations.
 *  - Matrix-validatie + same-folder-check gebeuren server-side; bij
 *    fout: error-banner in Foldertree (via ApiError).
 *
 * Volgende iteratie (eigen ticket):
 *  - Relation-type-picker dialog (geen prompt() meer).
 *  - Client-side matrix-validatie + visuele hint welke verbindingen
 *    geldig zijn.
 *  - Edge-label-render met relation-type-naam.
 */
import {
  applyNodeChanges,
  Background,
  Controls,
  type Edge,
  type Node,
  type NodeChange,
  type OnConnect,
  ReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "../../api/client";
import {
  filterRelationTypesByActiveExtensions,
  getRegistry,
  resolveBilingual,
  validRelationTypesBetween,
  validRelationTypesReversed,
} from "../../registry";
import { useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { ConceptNode, type ConceptNodeData } from "../canvas/ConceptNode";
import { applyRelationStyle } from "../canvas/edgeStyles";
import { RoutedEdge } from "../canvas/RoutedEdge";

const NODE_TYPES = { concept: ConceptNode };
const EDGE_TYPES = { routed: RoutedEdge };
const DEFAULT_NODE_W = 140;
const DEFAULT_NODE_H = 60;

const EXISTING_CONCEPT_MIME = "application/ae-existing-concept-id";

export function KladblokCanvas() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  // Alleen kijken: concepten erop leggen (lokaal) mag, verbinden niet — dat
  // maakt een relatie in het model.
  const readOnly = useSession((s) => s.readOnly);
  const selectConcept = useUi((s) => s.selectConcept);
  const setAttributePanelOpen = useUi((s) => s.setAttributePanelOpen);
  const revision = useWorkspaceData((s) => s.revision);
  void revision;

  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [error, setError] = useState<string | null>(null);
  // M6.5-W3b — picker-state voor relatie-keuze. Wordt gevuld bij
  // onConnect; gebruiker kiest dan een geldig relation-type uit een
  // gefilterde lijst (matrix-validatie client-side).
  const [pendingConnection, setPendingConnection] = useState<{
    source: string;
    target: string;
    sourceTypeId: string;
    targetTypeId: string;
    sourceStereotypes: readonly string[];
    targetStereotypes: readonly string[];
  } | null>(null);

  const handleDragOver = useCallback((ev: React.DragEvent) => {
    if (ev.dataTransfer.types.includes(EXISTING_CONCEPT_MIME)) {
      ev.preventDefault();
      ev.dataTransfer.dropEffect = "copy";
    }
  }, []);

  const handleDrop = useCallback(
    (ev: React.DragEvent) => {
      ev.preventDefault();
      const conceptId = ev.dataTransfer.getData(EXISTING_CONCEPT_MIME);
      if (!conceptId) return;
      const concept = useWorkspaceData.getState().store.getConcept(conceptId);
      if (!concept) {
        setError(t("app.kladblok.conceptNotFound", { conceptId }));
        return;
      }
      // Voorkom dubbele nodes op kladblok (concept staat al → focus op
      // bestaande in plaats van een duplicate creëren).
      if (nodes.some((n) => n.id === conceptId)) {
        selectConcept(conceptId);
        setAttributePanelOpen(true);
        return;
      }
      const bounds = (ev.currentTarget as HTMLElement).getBoundingClientRect();
      // Translate viewport coords naar canvas-coords (ReactFlow-default
      // viewport, geen zoom-correctie voor v1 — dat komt mee als we
      // de canvas een proper view-instance geven).
      const position = {
        x: ev.clientX - bounds.left,
        y: ev.clientY - bounds.top,
      };
      const label = resolveBilingual(concept.name, lang) || concept.id;
      const reg = getRegistry();
      const elementType = reg.getElementType(concept.typeId);
      const typeLabel = elementType ? resolveBilingual(elementType.label, lang) : concept.typeId;
      const data: ConceptNodeData = {
        label,
        layer: elementType?.layer ?? "composite",
        aspect: elementType?.aspect ?? "passive_structure",
        typeLabel,
        typeId: concept.typeId,
        conceptId: concept.id,
        stereotypes: concept.stereotypes ?? [],
        ...(concept.attributes ? { attributes: concept.attributes } : {}),
      };
      setNodes((prev) => [
        ...prev,
        {
          id: concept.id,
          type: "concept",
          position,
          data,
          width: DEFAULT_NODE_W,
          height: DEFAULT_NODE_H,
        },
      ]);
    },
    [nodes, lang, selectConcept, setAttributePanelOpen, t],
  );

  const handleNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  const handleConnect: OnConnect = useCallback(
    (params) => {
      if (!workspaceId || !branchId) return;
      if (useSession.getState().readOnly) return;
      if (!params.source || !params.target) return;
      const store = useWorkspaceData.getState().store;
      const src = store.getConcept(params.source);
      const tgt = store.getConcept(params.target);
      if (!src || !tgt) {
        setError(t("app.kladblok.endpointsNotFound"));
        return;
      }
      setPendingConnection({
        source: params.source,
        target: params.target,
        sourceTypeId: src.typeId,
        targetTypeId: tgt.typeId,
        sourceStereotypes: src.stereotypes ?? [],
        targetStereotypes: tgt.stereotypes ?? [],
      });
    },
    [workspaceId, branchId, t],
  );

  const createRelationFromPending = useCallback(
    async (relTypeId: string, reversed: boolean) => {
      if (!workspaceId || !branchId || !pendingConnection) return;
      if (useSession.getState().readOnly) return;
      const sourceId = reversed ? pendingConnection.target : pendingConnection.source;
      const targetId = reversed ? pendingConnection.source : pendingConnection.target;
      try {
        const rel = await api.createRelation(workspaceId, branchId, {
          type_id: relTypeId,
          source_concept_id: sourceId,
          target_concept_id: targetId,
        });
        const reg = getRegistry();
        const rt = reg.getRelationType(relTypeId);
        const baseEdge: Edge = {
          id: rel.id,
          source: sourceId,
          target: targetId,
          type: "routed",
          label: rt ? resolveBilingual(rt.label, lang) : relTypeId.replace(/^[a-z]+\./, ""),
        };
        // applyRelationStyle voegt markers (composition-diamond, etc.)
        // en line-color toe, conform de gewone canvas-render.
        const styled = applyRelationStyle(baseEdge, relTypeId, rel.attributes, rel.stereotypes);
        setEdges((prev) => [...prev, styled]);
        setPendingConnection(null);
      } catch (err) {
        const msg = err instanceof ApiError ? (err.body?.detail ?? err.message) : String(err);
        setError(t("app.kladblok.createRelationFailed", { msg }));
      }
    },
    [workspaceId, branchId, pendingConnection, t],
  );

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      selectConcept(node.id);
      setAttributePanelOpen(true);
    },
    [selectConcept, setAttributePanelOpen],
  );

  const clearKladblok = useCallback(() => {
    setNodes([]);
    setEdges([]);
  }, []);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: eigen kladblok-canvas-oppervlak; pointer-interactie, keyboard-a11y op canvas-niveau
    <div className="relative h-full w-full" onDragOver={handleDragOver} onDrop={handleDrop}>
      {error && (
        <div className="absolute top-2 right-2 left-2 z-10 rounded border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-xs">
          {error}
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-2 underline"
            aria-label={t("app.kladblok.dismissAria")}
          >
            {t("app.kladblok.dismiss")}
          </button>
        </div>
      )}
      <div className="absolute top-2 right-2 z-10 flex gap-1">
        <span className="rounded bg-white/80 px-2 py-1 text-slate-500 text-xs italic shadow">
          {t("app.kladblok.hint")}
        </span>
        {nodes.length > 0 && (
          <button
            type="button"
            onClick={clearKladblok}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-slate-600 text-xs hover:bg-slate-50"
            title={t("app.kladblok.clearTitle")}
          >
            {t("app.kladblok.clear")}
          </button>
        )}
      </div>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        onNodesChange={handleNodesChange}
        {...(readOnly ? {} : { onConnect: handleConnect })}
        nodesConnectable={!readOnly}
        onNodeClick={handleNodeClick}
        proOptions={{ hideAttribution: true }}
        fitView={false}
        minZoom={0.3}
        maxZoom={2}
      >
        <Background gap={16} />
        <Controls showInteractive={false} />
      </ReactFlow>
      {pendingConnection && (
        <RelationTypePickerDialog
          lang={lang}
          sourceTypeId={pendingConnection.sourceTypeId}
          targetTypeId={pendingConnection.targetTypeId}
          sourceStereotypes={pendingConnection.sourceStereotypes}
          targetStereotypes={pendingConnection.targetStereotypes}
          onPick={createRelationFromPending}
          onCancel={() => setPendingConnection(null)}
        />
      )}
    </div>
  );
}

function RelationTypePickerDialog({
  lang,
  sourceTypeId,
  targetTypeId,
  sourceStereotypes,
  targetStereotypes,
  onPick,
  onCancel,
}: {
  lang: string;
  sourceTypeId: string;
  targetTypeId: string;
  sourceStereotypes: readonly string[];
  targetStereotypes: readonly string[];
  onPick: (relTypeId: string, reversed: boolean) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const reg = getRegistry();
  // Drawing-primitieven (Lijn / Lijn met pijlpunt) zijn niet-semantische
  // hulplijnen — in een Foldertree-kladblok hebben ze niets te zoeken.
  const isDrawing = (id: string) => id.startsWith("drawing.");
  // Extensie-bewuste filter: c4.* / sharp.* / bmc.*-relaties alleen
  // tussen endpoints met een matching stereotype-prefix. Voorkomt dat
  // c4.Uses verschijnt op twee Sharp-«Concept»-objecten, etc.
  const forward = filterRelationTypesByActiveExtensions(
    validRelationTypesBetween(sourceTypeId, targetTypeId).filter((id) => !isDrawing(id)),
    sourceStereotypes,
    targetStereotypes,
  );
  const backward = filterRelationTypesByActiveExtensions(
    validRelationTypesReversed(sourceTypeId, targetTypeId).filter((id) => !isDrawing(id)),
    sourceStereotypes,
    targetStereotypes,
  );
  const entries: { id: string; label: string; reversed: boolean }[] = [];
  for (const id of forward) {
    const rt = reg.getRelationType(id);
    const label = rt ? resolveBilingual(rt.label, lang) : id;
    entries.push({ id, label, reversed: false });
  }
  for (const id of backward) {
    const rt = reg.getRelationType(id);
    const label = rt ? resolveBilingual(rt.label, lang) : id;
    entries.push({ id, label: t("app.kladblok.picker.reversed", { label }), reversed: true });
  }

  const [highlightIdx, setHighlightIdx] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Focus zodra de dialog opent zodat keyboard-events direct landen op
  // de dialog en niet door React Flow worden onderschept.
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  // Auto-scroll highlight in view bij keyboard-navigatie.
  useEffect(() => {
    itemRefs.current[highlightIdx]?.scrollIntoView({ block: "nearest" });
  }, [highlightIdx]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
      return;
    }
    if (entries.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => (i + 1) % entries.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => (i - 1 + entries.length) % entries.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const sel = entries[highlightIdx];
      if (sel) onPick(sel.id, sel.reversed);
    }
  };

  // Portal naar document.body: zonder dit eet ReactFlow de mouse-click,
  // wheel- en keyboard-events op de dialog. Buiten z'n event-tree
  // landen ze normaal in onze handlers.
  const node = (
    // biome-ignore lint/a11y/noStaticElementInteractions: eigen kladblok-canvas-oppervlak; pointer-interactie, keyboard-a11y op canvas-niveau
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: eigen kladblok-canvas-oppervlak; pointer-interactie, keyboard-a11y op canvas-niveau */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        className="w-80 max-w-[90vw] rounded-lg border border-slate-300 bg-white shadow-xl outline-none"
      >
        <header className="border-slate-200 border-b px-4 py-2 font-semibold text-slate-900 text-sm">
          {t("app.kladblok.picker.title")}
        </header>
        <div className="max-h-72 overflow-y-auto px-2 py-2">
          {entries.length === 0 ? (
            <p className="px-2 py-3 text-slate-500 text-xs italic">
              {t("app.kladblok.picker.noneBefore")}{" "}
              <code className="font-mono">{sourceTypeId.replace(/^[a-z]+\./, "")}</code>{" "}
              {t("app.kladblok.picker.noneAnd")}{" "}
              <code className="font-mono">{targetTypeId.replace(/^[a-z]+\./, "")}</code>{" "}
              {t("app.kladblok.picker.noneAfter")}
            </p>
          ) : (
            // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: ARIA-listbox-pattern voor relatie-type-keuze; ul + role="listbox" met li-children is een standaard ARIA-recept
            <ul className="flex flex-col gap-0.5" role="listbox">
              {entries.map((entry, idx) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: entry-id kan dupliceren binnen de lijst, vandaar de index-discriminator; lijst wordt volledig herberekend
                <li key={`${entry.id}-${idx}`}>
                  <button
                    type="button"
                    ref={(el) => {
                      itemRefs.current[idx] = el;
                    }}
                    onClick={() => onPick(entry.id, entry.reversed)}
                    onMouseEnter={() => setHighlightIdx(idx)}
                    className={`block w-full rounded px-2 py-1 text-left text-sm ${
                      idx === highlightIdx
                        ? "bg-blue-100 text-blue-900"
                        : "text-slate-700 hover:bg-blue-50"
                    }`}
                  >
                    {entry.label}
                    <span className="ml-2 text-slate-400 text-xs">{entry.id}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <footer className="flex justify-end gap-2 border-slate-200 border-t bg-slate-50 px-3 py-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-slate-300 bg-white px-3 py-1 text-slate-700 text-xs hover:bg-slate-100"
          >
            {t("settings.common.cancel")}
          </button>
        </footer>
      </div>
    </div>
  );
  return createPortal(node, document.body);
}
